import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleApiError, type U3129InitLU } from "./U3129Service";
import { U3129ContextProvider } from "./U3129Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U3129EmployeeNotConfigured from "./components/U3129EmployeeNotConfigured";

export const NS = "u3129";

export const U3129ROLES = {
  READ: "U3129:READ",
  ADD: "U3129:ADD",
  EDIT: "U3129:EDIT",
  DELETE: "U3129:DELETE",
  AUDITS: "U3129:AUDITS",
  OTHER_EMPLOYEE: "U3129:OTHEREMPLOYEE",
} as const;
export type U3129ROLES = (typeof U3129ROLES)[keyof typeof U3129ROLES];

const defaultInitLU: U3129InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  defaultPeriodPreset: "current_week",
  equipmentTypes: [],
  taxRates: [],
  expenseTypes: [],
  personalization: {
    admIncludeSubProject: null,
    skipHourBankBalanceValidationOnSubmit: false,
    hideExpenseAndEquipmentTabs: false,
  },
  employee: null,
};

const U3129: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3129InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces([NS], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3129InitLU>({
    doInitLUFn: async () => await doInitLU(u2000AxiosAuthenticated),
    onSuccess: (payload: U3129InitLU) => {
      // Ne pas conserver tasks : honoraires = findTasksForProject (évite données et mémoire inutiles).
      const rest = { ...payload };
      delete rest.tasks;
      setInitLUData(rest);
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUData(defaultInitLU);
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init LU au montage (handlers stables)
  }, [t]);

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  if (initLUData.employee == null) {
    return <U3129EmployeeNotConfigured />;
  }

  return (
    <U3129ContextProvider initLU={initLUData}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    </U3129ContextProvider>
  );
};

export default U3129;
